import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import R from 'ramda';
import Picture from '@cotyorg/ccx-atoms/Picture';
import { Carousel } from '@cotyorg/ccx-organism-carousel';
import Modal from 'react-modal';
import ReactPlayer from 'react-player';
import {
  trackVideoPlayEvent,
  trackVideoMilestoneEvent,
} from '@cotyorg/ccx-utils/js/tracking/adobe-dtm/events/video';
import { BeforeAfterSlider } from '@cotyorg/ccx-molecules/BeforeAfterSlider/BeforeAfterSlider';

Modal.setAppElement('#app');

export const getPercentage = ({ progress }) =>
  Math.floor(parseFloat(R.prop('played', progress)).toFixed(3) * 100);

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    zIndex: '10000',
    overflow: 'initial',
    background: 'transparent',
    border: 'none',
    padding: '30px 20px 0 20px',
  },
  overlay: {
    zIndex: '10',
  },
};

const modalSmallBreakpointStyles = {
  content: {
    ...modalStyles.content,
    padding: '30px 0 0 0',
  },
  overlay: {
    zIndex: '10',
  },
};

const closeButtonStyles = {
  position: 'absolute',
  background: 'transparent',
  border: 'none',
  right: '5px',
  top: 0,
  padding: 0,
  margin: 0,
};

const smallBreakpointButtonStyles = {
  ...closeButtonStyles,
  right: '10px',
};

const isSmallBreakpoint = currentBreakpoint =>
  currentBreakpoint && currentBreakpoint.includes('small');

const pictureComponents = ({
  images,
  currentBreakpoint,
  disableProgressiveLoading,
  onPictureClick,
  openBeforeAfterSliderInModal,
}) =>
  Array.isArray(images) &&
  images.map(({
    src, srcset, alt, videoUrl, videoTitle, beforeAfterSlider,
  }) => {
    if (videoUrl) {
      return (
        <button
          className="picture__image-button"
          onClick={() => onPictureClick({ videoUrl, videoTitle })}
        >
          <Picture
            key={src}
            currentBreakpoint={currentBreakpoint}
            src={src}
            srcset={srcset}
            alt={alt}
            disableProgressiveLoading={disableProgressiveLoading}
          />
        </button>
      );
    }

    if (!!beforeAfterSlider && !openBeforeAfterSliderInModal) {
      return (
        <BeforeAfterSlider
          beforeAfterSlider={beforeAfterSlider}
          currentBreakpoint={currentBreakpoint}
        />
      );
    }
    if (!!beforeAfterSlider && openBeforeAfterSliderInModal) {
      return (
        <button
          className="picture__image-button"
          onClick={() => onPictureClick({ beforeAfterSlider })}
        >
          <Picture
            key={src}
            currentBreakpoint={currentBreakpoint}
            src={src}
            srcset={srcset}
            alt={alt}
            disableProgressiveLoading={disableProgressiveLoading}
          />
        </button>
      );
    }

    return (
      <Picture
        key={src}
        currentBreakpoint={currentBreakpoint}
        src={src}
        srcset={srcset}
        alt={alt}
        disableProgressiveLoading={disableProgressiveLoading}
      />
    );
  });

const ProductGalleryThumbnails = ({
  selectedGallery,
  headingLevel,
  rightChevronIconSrc,
  leftChevronIconSrc,
  currentBreakpoint,
  disableProgressiveLoading,
  openBeforeAfterSliderInModal,
  ...props
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actualVideo, setActualVideo] = useState({
    url: '',
    title: '',
  });
  const [beforeAfterSlider, setBeforeAfterSlider] = useState();
  const [videoDuration, setVideoDuration] = useState(null);
  const [videoProgress, setVideoProgress] = useState(null);
  const [videoMilestone, setVideoMilestone] = useState(0);

  useEffect(() => {
    if (videoMilestone > 0) {
      trackVideoMilestoneEvent({
        url: R.prop('url', actualVideo),
        duration: videoDuration,
        progress: videoProgress,
        videoMilestone,
        title: R.prop('title', actualVideo),
      });
    }
  }, [videoMilestone]);

  // Hide modal when current breakpoint changes and InModal option is disabled
  useEffect(() => {
    if (!!beforeAfterSlider && !openBeforeAfterSliderInModal && isModalOpen) {
      setIsModalOpen(false);
    }
  }, [currentBreakpoint]);

  const isSmallDevice = isSmallBreakpoint(currentBreakpoint);

  return (
    <Fragment>
      <div className="product-gallery">
        <Carousel
          headingLevel={headingLevel}
          carouselsStoreDataKey="productGallery"
          rightChevronIconSrc={rightChevronIconSrc}
          leftChevronIconSrc={leftChevronIconSrc}
          pagination={{
            galleryData: selectedGallery,
            type: isSmallDevice ? 'lines' : 'thumbnails',
          }}
          {...props}
        >
          {pictureComponents({
            images: R.prop('images', selectedGallery),
            currentBreakpoint,
            disableProgressiveLoading,
            openBeforeAfterSliderInModal,
            onPictureClick: ({ videoUrl, videoTitle, beforeAfterSlider: beforeAfterSliderData }) => {
              setIsModalOpen(true);
              setActualVideo({
                url: videoUrl,
                title: videoTitle,
              });
              setBeforeAfterSlider(beforeAfterSliderData);
            },
          })}
        </Carousel>
      </div>

      <Modal
        style={isSmallDevice ? modalSmallBreakpointStyles : modalStyles}
        isOpen={isModalOpen}
        onRequestClose={() => {
          setIsModalOpen(false);
          setActualVideo({ url: '', title: '' });
          setVideoDuration(null);
          setVideoProgress(null);
          setVideoMilestone(0);
        }}
      >
        <button
          style={
            isSmallDevice ? smallBreakpointButtonStyles : closeButtonStyles
          }
          onClick={() => setIsModalOpen(false)}
        >
          <img
            alt="video player close button"
            width={16}
            height={16}
            src="/ccx-files/assets/cross.svg"
          />
        </button>

        {/* Force player rerender to trigger events if user plays
        video multiple times */}
        {isModalOpen && !!R.prop('url', actualVideo) && (
          <div className="player-wrapper">
            <ReactPlayer
              url={R.prop('url', actualVideo)}
              controls
              height={isSmallDevice ? '100%' : '360px'}
              width={isSmallDevice ? '100%' : '640px'}
              playing
              muted
              onProgress={(progress) => {
                const percentage = getPercentage({ progress });

                if (percentage > videoMilestone) {
                  if (percentage > 96) {
                    setVideoMilestone(100);
                  } else if (percentage > 76) {
                    setVideoMilestone(75);
                  } else if (percentage > 51) {
                    setVideoMilestone(50);
                  } else if (percentage > 26) {
                    setVideoMilestone(25);
                  } else {
                    setVideoMilestone(0);
                  }
                }

                setVideoProgress(progress);
              }}
              onPlay={() => {
                if (videoDuration && videoProgress === null) {
                  trackVideoPlayEvent({
                    url: R.prop('url', actualVideo),
                    duration: videoDuration,
                    progress: videoProgress,
                    title: R.prop('title', actualVideo),
                  });
                }
              }}
              onDuration={(duration) => {
                setVideoDuration(parseInt(duration, 10));
              }}
            />
          </div>
        )}

        {isModalOpen && beforeAfterSlider && openBeforeAfterSliderInModal && (
          <BeforeAfterSlider
            beforeAfterSlider={beforeAfterSlider}
            currentBreakpoint={currentBreakpoint}
          />
        )}
      </Modal>
    </Fragment>
  );
};

ProductGalleryThumbnails.propTypes = {
  selectedGallery: PropTypes.shape({
    images: PropTypes.array.isRequired,
    sku: PropTypes.string.isRequired,
  }).isRequired,
  headingLevel: PropTypes.number,
  rightChevronIconSrc: PropTypes.string,
  leftChevronIconSrc: PropTypes.string,
  currentBreakpoint: PropTypes.string.isRequired,
  disableProgressiveLoading: PropTypes.bool,
  openBeforeAfterSliderInModal: PropTypes.bool,
};

export default ProductGalleryThumbnails;
